"use client";

import { motion, useMotionValue, useTransform } from "framer-motion";
import { MouseEvent } from "react";
import Magnetic from "./ui/Magnetic";

const testimonials = [
  {
    content: "Amrit is an exceptional engineer who builds backend microservices and databases architectures with rigorous attention to detail. During our product launches, his automated pipelines and cache configurations kept API response latencies below 20ms under heavy concurrent user spikes.",
    author: "Rohan Mehta",
    role: "Senior Engineering Lead",
    company: "TechScale Solutions",
    relation: "1st • Managed Amrit directly",
    date: "March 12, 2026",
    avatarText: "RM",
    platform: "LinkedIn Recommendation"
  },
  {
    content: "Working with Amrit on our CCTV transcoding infrastructure was a game changer. He took ownership of the FFmpeg streaming pipelines, packaged the services inside Docker containers, and routed them behind Traefik reverse proxies safely. His system design skills are top-tier.",
    author: "Siddharth Verma",
    role: "Digital Solutions Architect",
    company: "Haryana Municipal Board Project",
    relation: "1st • Worked alongside Amrit",
    date: "September 24, 2025",
    avatarText: "SV",
    platform: "Infosys Project Feedback"
  },
  {
    content: "Amrit has a deep understanding of core web performance benchmarks. He reworked our React dashboard states, implemented route prefetching, and set up strict TypeScript interfaces. Our web vitals score reached 100/100, and codebase readabilities improved drastically.",
    author: "Elena Rostova",
    role: "Founding Principal Architect",
    company: "Apex SaaS Platform",
    relation: "1st • Client partner",
    date: "January 18, 2026",
    avatarText: "ER",
    platform: "Client Review"
  }
];

function TestimonialCard({ testimonial, index }: { testimonial: typeof testimonials[0], index: number }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 20 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true }}
      onMouseMove={handleMouseMove}
      className="group relative overflow-hidden rounded-[2.5rem] border border-white/5 bg-white/[0.02] p-8 md:p-10 hover:border-indigo-500/40 transition-all duration-500 shadow-2xl flex flex-col justify-between"
    >
      {/* Glow spotlight */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-[2.5rem] opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useTransform(
            [mouseX, mouseY],
            ([x, y]) => `radial-gradient(450px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.08), transparent 85%)`
          ),
        }}
      />
      
      <div>
        {/* Platform tag header */}
        <div className="flex items-center justify-between mb-6 relative z-10">
          <span className="text-[9px] font-mono font-bold tracking-widest text-indigo-400 uppercase">
            {testimonial.platform}
          </span>
          <span className="text-[10px] text-gray-500 font-mono">{testimonial.date}</span>
        </div>

        {/* Quote text */}
        <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-8 relative z-10 italic">
          &ldquo;{testimonial.content}&rdquo;
        </p>
      </div>

      {/* Author specs */}
      <div className="flex items-center gap-4 relative z-10 border-t border-white/5 pt-6 mt-6">
        <Magnetic>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center font-black text-white text-sm shadow-xl select-none">
            {testimonial.avatarText}
          </div>
        </Magnetic>
        <div>
          <h4 className="text-sm font-bold text-white group-hover:text-indigo-400 transition-colors duration-300 tracking-tight flex items-center gap-1.5">
            <span>{testimonial.author}</span>
            <svg className="w-3.5 h-3.5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a.75.75 0 00-.708-.523H4.5a.75.75 0 00-.75.75v1.847c0 .354.12.695.34 1.004l3.75 5.25a.75.75 0 001.218 0l3.75-5.25a1.25 1.25 0 00.34-1.004V3.682a.75.75 0 00-.75-.75h-1.059a.75.75 0 00-.708.523L9.61 5.923a.75.75 0 01-1.22 0L6.267 3.455z" clipRule="evenodd" />
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4.13-5.69z" />
            </svg>
          </h4>
          <p className="text-gray-400 text-xs font-semibold leading-none mt-1">
            {testimonial.role} @ <span className="text-gray-500">{testimonial.company}</span>
          </p>
          <span className="text-[9px] text-gray-600 font-mono tracking-wider block mt-1">
            {testimonial.relation}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export default function Testimonials() {
  return (
    <section id="testimonials" className="section-pad relative z-10 overflow-hidden bg-black/15 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-xs font-bold tracking-[0.5em] text-indigo-400 uppercase mb-6 block">Recommendations</span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white font-[var(--font-poppins)] tracking-tighter">
            Peer <span className="gradient-text">Validation</span>
          </h2>
          <p className="mt-6 text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            Testimonials and code audits evaluations from engineers and founders who have verified my deployments.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, i) => (
            <TestimonialCard key={testimonial.author} testimonial={testimonial} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
